import React, { useContext, useEffect, useState } from 'react';
import { Message } from 'talkplus-sdk/dist/model/types';
import MyMessage from '@components/chat/chatDetail/messageList/myMessage';
import AdminMessage from '@components/chat/chatDetail/messageList/adminMessage';
import OtherUsersMessage from '@components/chat/chatDetail/messageList/otherUsersMessage';
import BottomButton from '@components/chat/chatDetail/messageList/bottomButton';
import useScroll from '@components/chat/hooks/useScroll';
import { ChatContext, ChatContextType } from '@components/chat/ChatContext';
import DateLine from '@components/chat/chatDetail/messageList/dateLine';
import CompanyBtn from '@components/chat/chatDetail/messageList/companyBtn';
import DateUtil from '@utils/date/util';
import classnames from 'classnames/bind';
import styles from './index.module.scss';

const cx = classnames.bind(styles);

interface MessageListProps {
  useScrollProps: ReturnType<typeof useScroll>;
  handleReply: (message: Message) => void;
  isReplying?: boolean;
}

const MessageList = ({ useScrollProps, handleReply, isReplying }: MessageListProps) => {
  const { userData, channel, messagesData, fetchNextMessages, isLoadingNextMessages } =
    useContext<ChatContextType>(ChatContext);
  const { ref, isTop, isBottom, toScrollBottom, toScrollPrev, getIsScroll } = useScrollProps;

  const [listVisible, setListVisible] = useState<boolean>(false);

  const isLoading = !messagesData;

  useEffect(() => {
    if (!isLoading) {
      setTimeout(() => {
        toScrollBottom();
        setListVisible(true);
      }, 10);
    }
  }, [isLoading]);

  useEffect(() => {
    if (isTop) {
      if (!channel) return;
      fetchNextMessages({ channelId: channel.id });
    }
  }, [isTop]);

  useEffect(() => {
    if (!isLoadingNextMessages) {
      toScrollPrev();
    }
  }, [isLoadingNextMessages]);

  const getMessageEls = () => {
    const isMyMessage = (message: Message) => message.userId === userData?.user.id;

    return messagesData?.messages.map((message, index) => {
      const prevMessage = index > 0 ? messagesData?.messages[index - 1] : messagesData?.messages[0];

      const getIsReactionsPrevMessage = () => {
        if (index === 0) return false;
        return Object.keys(prevMessage?.reactions || {}).length > 0;
      };

      const getName = () => {
        if (index === 0) return message.username;
        if (!DateUtil.areDatesEqual(new Date(prevMessage?.createdAt || new Date()), new Date(message.createdAt)))
          return message.username;
        return prevMessage?.userId !== message.userId ? message.username : undefined;
      };

      const getDate = () => {
        if (index === 0) return new Date(message.createdAt);
        return DateUtil.areDatesEqual(new Date(prevMessage?.createdAt || new Date()), new Date(message.createdAt))
          ? undefined
          : new Date(message.createdAt);
      };

      const getMessageProps = () => {
        return {
          message,
          name: getName(),
          isReplying,
          handleReply,
        };
      };

      if (isMyMessage(message)) {
        return (
          <React.Fragment key={message.id + index}>
            <DateLine date={getDate()} isMarginTop={getIsReactionsPrevMessage()} />
            <MyMessage {...getMessageProps()} />
          </React.Fragment>
        );
      }
      if (message.type === 'admin') {
        return (
          <React.Fragment key={message.id + index}>
            <DateLine date={getDate()} isMarginTop={getIsReactionsPrevMessage()} />
            <AdminMessage {...getMessageProps()} />
          </React.Fragment>
        );
      }
      if (message.type === 'text') {
        return (
          <React.Fragment key={message.id + index}>
            <DateLine date={getDate()} isMarginTop={getIsReactionsPrevMessage()} />
            <OtherUsersMessage {...getMessageProps()} />
          </React.Fragment>
        );
      }
      return null;
    });
  };

  return (
    <div className={cx('messageListArea')}>
      <div className={cx('listArea')}>
        {/* chatList: 스크롤 이벤트 감지를 위한 id */}
        <ul ref={ref} id={'chatList'} className={cx('listWrap', { visible: listVisible })}>
          {getMessageEls()}
          {isReplying && <div className={cx('replying')} />}
        </ul>
      </div>
      <CompanyBtn channelId={channel?.id} />
      <BottomButton visible={!isBottom && getIsScroll()} onClick={toScrollBottom} />
    </div>
  );
};

export default MessageList;
